import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock as ClockIcon, RefreshCw, Loader2, Zap, Wrench, Copy, X, Send } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};
const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const SETUP_LABELS = {
  scheduled: "Programmé",
  complete: "Complet",
  processing: "En cours",
  initiated: "Initié",
  open: "Ouvert",
  canceled: "Annulé",
  expired: "Expiré",
};
const copy = (v) => { if (!v) return; navigator.clipboard?.writeText(String(v)); toast.success("Copié"); };

const InfoRow = ({ label, value, mono = false, copyable = true }) => (
  <div className="flex items-center gap-2 py-1.5 border-b border-stone-200 last:border-0">
    <span className="text-xs text-stone-500 min-w-[140px]">{label}</span>
    <span className={`flex-1 text-xs break-all ${mono ? "font-mono" : ""} text-stone-800`}>{value || "—"}</span>
    {copyable && value && (
      <button type="button" onClick={() => copy(value)} className="text-stone-400 hover:text-deep-rose" title="Copier">
        <Copy size={12} />
      </button>
    )}
  </div>
);

const ManualAttachPanel = ({ row, onClose, onDone }) => {
  const id = row.id || row.txId || row.stripeSessionId;
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [scheduleId, setScheduleId] = useState("");
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingInfo(true);
      try {
        const { data } = await api.get(`/admin/payments/sepa-manual-info/${id}`);
        if (alive) setInfo(data);
      } catch (e) {
        toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
      } finally { if (alive) setLoadingInfo(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const attach = async () => {
    const val = scheduleId.trim();
    if (!val) { toast.error("Collez le SubscriptionSchedule ID"); return; }
    setAttaching(true);
    try {
      const { data } = await api.post(`/admin/payments/sepa-attach/${id}`, { scheduleId: val });
      toast.success(`Abonnement rattaché : ${data.stripeSubscriptionScheduleId || data.stripeSubscriptionId}`);
      onDone?.();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setAttaching(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-stone-200 w-full" data-testid={`sepa-manual-panel-${id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-stone-800 inline-flex items-center gap-1.5">
          <Wrench size={14} className="text-deep-rose"/> Création manuelle dans Stripe
        </div>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-700" title="Fermer"><X size={14}/></button>
      </div>

      {loadingInfo ? (
        <div className="text-xs text-stone-500 inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Récupération des infos Stripe…</div>
      ) : info ? (
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-stone-200 p-3">
            <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-1">1. Infos à utiliser dans Stripe</div>
            <InfoRow label="Customer" value={info.stripeCustomerId} mono />
            <InfoRow label="Payment method" value={info.stripePaymentMethodId} mono />
            <InfoRow label="Montant mensuel" value={`${fmtEur(info.monthlyAmount)} (${info.monthlyAmountCents} centimes)`} copyable={false} />
            <InfoRow label="Itérations" value={String(info.iterations || 8)} copyable={false} />
            <InfoRow label="Date 1er débit" value={info.firstDebitAt} copyable={false} />
            <InfoRow label="Session Stripe" value={info.sessionStatus} copyable={false} />
            {info.stripeCustomerId && (
              <a
                href={`https://dashboard.stripe.com/customers/${info.stripeCustomerId}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs text-deep-rose hover:underline mt-1 inline-block"
              >
                Ouvrir le client dans Stripe →
              </a>
            )}
          </div>

          <div className="bg-white rounded-xl border border-stone-200 p-3">
            <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">2. Rattacher l&apos;abonnement créé</div>
            <ol className="text-xs text-stone-600 list-decimal ml-4 space-y-1 mb-3">
              <li>Sur Stripe Dashboard, ouvrez le <strong>customer</strong> ci-dessus.</li>
              <li>« Actions » → <strong>Create subscription</strong> (mode API/Test si besoin).</li>
              <li>Choisissez le <strong>payment method</strong> ci-dessus, un tarif récurrent mensuel du montant indiqué, {info.iterations || 8} itérations, <strong>collection method : charge automatically</strong>, <strong>start date : {info.firstDebitAt || "1er octobre"}</strong>.</li>
              <li>Copiez l&apos;ID de la subscription créée (<span className="font-mono">sub_sched_…</span> ou <span className="font-mono">sub_…</span>) et collez-le ci-dessous.</li>
            </ol>
            <label className="block text-xs text-stone-500 mb-1">SubscriptionSchedule ID (ou Subscription ID)</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="sub_sched_XXXXXXXXXXXX"
                value={scheduleId}
                onChange={(e) => setScheduleId(e.target.value)}
                data-testid={`sepa-schedule-input-${id}`}
                className="flex-1 min-w-0 font-mono text-sm border border-stone-300 rounded-lg px-3 py-2 focus:outline-none focus:border-deep-rose"
              />
              <button
                onClick={attach}
                disabled={attaching || !scheduleId.trim()}
                data-testid={`sepa-attach-btn-${id}`}
                className="shrink-0 inline-flex items-center gap-1.5 bg-deep-rose hover:brightness-110 text-white px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-40 border-2 border-deep-rose disabled:border-stone-300"
              >
                {attaching ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>}
                Rattacher
              </button>
            </div>
            <div className="text-[11px] text-stone-400 mt-1">
              Le bouton s&apos;active dès que vous collez un ID commençant par <span className="font-mono">sub_sched_</span> ou <span className="font-mono">sub_</span>.
            </div>
          </div>
        </div>
      ) : (
        <div className="text-xs text-red-700">Impossible de récupérer les infos Stripe.</div>
      )}
    </div>
  );
};

const SepaDiagnosticAdmin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const [openManual, setOpenManual] = useState({});
  const [stuckCount, setStuckCount] = useState(0);
  const [searchParams] = useSearchParams();
  const highlightTxId = searchParams.get("tx") || "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payments/sepa-diagnostic");
      setRows(data.transactions || []);
      setStuckCount(data.stuckCount || 0);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-scroll to the highlighted row after loading, and pre-open its manual attach panel.
  useEffect(() => {
    if (!highlightTxId || loading || !rows.length) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="sepa-row-${highlightTxId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [highlightTxId, loading, rows.length]);

  const retry = async (row) => {
    const id = row.id || row.txId || row.stripeSessionId;
    setRetrying((prev) => ({ ...prev, [id]: true }));
    try {
      const { data } = await api.post(`/admin/payments/sepa-retry/${id}`);
      if (data.stripeSubscriptionScheduleId) {
        toast.success(`Subscription créée : ${data.stripeSubscriptionScheduleId}`);
      } else {
        toast.info(data.message || "Traitement effectué");
      }
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setRetrying((prev) => ({ ...prev, [id]: false })); }
  };

  const [resending, setResending] = useState({});
  const [detaching, setDetaching] = useState({});
  const resendMandate = async (row) => {
    const id = row.id;
    if (!window.confirm(`Renvoyer un nouveau lien SEPA à ${row.familyEmail} ?\n\nUn email sera envoyé avec un nouveau lien de signature. Le lien précédent sera invalidé.`)) return;
    setResending((prev) => ({ ...prev, [id]: true }));
    try {
      const { data } = await api.post(`/admin/payments/sepa-resend-mandate/${id}`);
      if (data.emailed) {
        toast.success(data.message || `Lien envoyé à ${row.familyEmail}`);
      } else {
        toast.info(data.message || "Lien créé mais email non envoyé");
      }
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setResending((prev) => ({ ...prev, [id]: false })); }
  };

  const detach = async (row) => {
    const id = row.id;
    if (!window.confirm(`Détacher l'ID Stripe « ${row.stripeSubscriptionScheduleId} » de cette transaction ?\n\nLa commande redeviendra "en attente" et vous pourrez rattacher un nouvel abonnement.`)) return;
    setDetaching((prev) => ({ ...prev, [id]: true }));
    try {
      await api.post(`/admin/payments/sepa-detach/${id}`);
      toast.success("ID Stripe détaché — vous pouvez rattacher un nouvel abonnement.");
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setDetaching((prev) => ({ ...prev, [id]: false })); }
  };

  return (
    <div data-testid="sepa-diagnostic-page">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Diagnostic SEPA</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Vérification des prélèvements 8x et détection des mandats bloqués (webhook Stripe manqué).</p>
        </div>
        <button onClick={load} disabled={loading} data-testid="sepa-refresh" className="inline-flex items-center gap-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 px-3 py-1.5 rounded-full text-xs">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""}/> Rafraîchir
        </button>
      </div>

      {stuckCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl px-4 py-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5"/>
          <div className="text-sm">
            <strong>{stuckCount}</strong> transaction{stuckCount > 1 ? "s" : ""} SEPA bloquée{stuckCount > 1 ? "s" : ""} — mandat signé mais aucune subscription Stripe créée.
            <div className="mt-1 text-amber-800/90">
              Essayez d&apos;abord « Réessayer » (crée l&apos;abonnement automatiquement). Si ça échoue,
              cliquez « Créer manuellement » pour créer l&apos;abonnement dans Stripe puis le rattacher au système.
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-stone-500"><Loader2 className="mx-auto animate-spin"/> Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-stone-500">Aucune transaction SEPA en base.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const id = r.id || r.txId || r.stripeSessionId;
            const isRetrying = retrying[id];
            const isHighlighted = highlightTxId && (highlightTxId === id || highlightTxId === r.stripeTxId);
            const bg = isHighlighted
              ? "bg-blush-light/70 border-2 border-deep-rose"
              : r.needsAttention ? "bg-amber-50 border border-amber-200" : "bg-white";
            return (
              <div key={id} className={`${bg} rounded-2xl shadow-sm p-4`} data-testid={`sepa-row-${id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-blush-light text-deep-rose">
                    {r.scheduleInvalid ? <AlertTriangle size={18}/> : r.scheduleVerified ? <CheckCircle2 size={18}/> : r.needsAttention ? <AlertTriangle size={18}/> : <ClockIcon size={18}/>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif-display text-stone-800">{r.parentName || "—"}</span>
                      {r.orderNumber && <span className="text-xs text-stone-700 font-mono bg-stone-100 rounded-full px-2 py-0.5">{r.orderNumber}</span>}
                      {r.scheduleInvalid && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-white bg-red-500 rounded-full px-2 py-0.5" data-testid={`sepa-invalid-badge-${id}`}>
                          <AlertTriangle size={10}/> {["canceled", "released"].includes(r.liveScheduleStatus) ? "Annulé dans Stripe" : "ID Stripe invalide"}
                        </span>
                      )}
                      {r.stripeSubscriptionScheduleId && !r.scheduleVerified && !r.scheduleInvalid && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-white bg-amber-500 rounded-full px-2 py-0.5" data-testid={`sepa-unverified-badge-${id}`}>
                          <AlertTriangle size={10}/> Non vérifié
                        </span>
                      )}
                    </div>
                    <div className="text-stone-500 text-xs mt-0.5">
                      {r.familyEmail} · {fmtDate(r.createdAt)} · {fmtEur(r.monthlyAmount)} × {r.iterations || 8}
                    </div>
                    <div className={`text-xs mt-1 ${r.needsAttention ? "text-amber-800 font-medium" : "text-stone-500"}`}>
                      {r.reason}
                    </div>
                    {r.lastScheduleError && (
                      <div className="text-xs mt-1 text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 inline-flex items-start gap-2 max-w-full">
                        <span className="break-words flex-1">
                          <strong>Erreur Stripe :</strong> {r.lastScheduleError}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await api.post(`/admin/payments/sepa-clear-error/${id}`);
                              toast.success("Erreur effacée");
                              await load();
                            } catch (e) {
                              toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
                            }
                          }}
                          data-testid={`sepa-clear-error-${id}`}
                          className="shrink-0 text-red-500 hover:text-red-800"
                          title="Effacer cette erreur"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                    {r.scheduleVerified && r.stripeSubscriptionScheduleId && !r.scheduleInvalid && (
                      <a href={`https://dashboard.stripe.com/subscription_schedules/${r.stripeSubscriptionScheduleId}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-deep-rose hover:underline mt-0.5 inline-block" data-testid={`sepa-stripe-link-${id}`}>
                        Voir dans Stripe →
                      </a>
                    )}
                    {r.stripeSubscriptionScheduleId && !r.scheduleVerified && (
                      <div className={`text-xs mt-1 border rounded px-2 py-1 max-w-full ${r.scheduleInvalid ? "text-red-700 bg-red-50 border-red-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
                        <strong>ID Stripe stocké :</strong> <span className="font-mono break-all">{r.stripeSubscriptionScheduleId}</span>
                        {r.scheduleInvalid ? (
                          <div className="mt-0.5 text-red-600">Cette référence n&apos;existe plus / a été annulée dans Stripe. Détachez-la puis rattachez un nouvel abonnement.</div>
                        ) : (
                          <div className="mt-0.5">Statut non vérifié (Stripe injoignable ou clé invalide). Détachez si vous savez que l&apos;ID est incorrect.</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs text-stone-500 shrink-0 min-w-[130px]">
                    <div>Setup : <span className={r.setupStatus === "complete" ? "text-emerald-700" : r.setupStatus === "canceled" ? "text-red-700 font-medium" : "text-amber-700"}>{SETUP_LABELS[r.setupStatus] || r.setupStatus || "—"}</span></div>
                    {r.liveStatus && (
                      <div>Stripe live : <span className={r.liveStatus === "complete" ? "text-emerald-700 font-medium" : r.liveStatus === "expired" ? "text-red-700" : "text-amber-700"}>{r.liveStatus}</span></div>
                    )}
                    <div>Prélevés : {r.iterationsPaid || 0} / {r.iterations || 8}</div>
                    <div>1er débit : {r.firstDebitAt ? fmtDate(r.firstDebitAt) : "—"}</div>
                  </div>
                  {r.canDetach && (r.scheduleInvalid || !r.scheduleVerified) && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => detach(r)}
                        disabled={detaching[id]}
                        data-testid={`sepa-detach-${id}`}
                        className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-full text-xs disabled:opacity-50"
                      >
                        {detaching[id] ? <Loader2 size={12} className="animate-spin"/> : <X size={12}/>}
                        Détacher
                      </button>
                    </div>
                  )}
                  {r.needsAttention && !r.stripeSubscriptionScheduleId && (
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <button
                        onClick={() => retry(r)}
                        disabled={isRetrying}
                        data-testid={`sepa-retry-${id}`}
                        className="inline-flex items-center gap-1.5 bg-coral hover:brightness-105 text-white px-4 py-2 rounded-full text-xs disabled:opacity-50"
                      >
                        {isRetrying ? <Loader2 size={12} className="animate-spin"/> : <Zap size={12}/>}
                        Réessayer
                      </button>
                      <button
                        onClick={() => setOpenManual((p) => ({ ...p, [id]: !p[id] }))}
                        data-testid={`sepa-manual-toggle-${id}`}
                        className="inline-flex items-center gap-1.5 bg-white border border-stone-300 hover:border-deep-rose text-stone-700 px-4 py-2 rounded-full text-xs"
                      >
                        <Wrench size={12}/> {openManual[id] ? "Fermer" : "Créer manuellement"}
                      </button>
                    </div>
                  )}
                  {r.canResend && !r.needsAttention && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => resendMandate(r)}
                        disabled={resending[id]}
                        data-testid={`sepa-resend-${id}`}
                        className="inline-flex items-center gap-1.5 bg-deep-rose hover:brightness-110 text-white px-4 py-2 rounded-full text-xs disabled:opacity-50"
                      >
                        {resending[id] ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
                        Renvoyer lien SEPA
                      </button>
                    </div>
                  )}
                </div>
                {openManual[id] && r.needsAttention && (
                  <ManualAttachPanel
                    row={r}
                    onClose={() => setOpenManual((p) => ({ ...p, [id]: false }))}
                    onDone={async () => { setOpenManual((p) => ({ ...p, [id]: false })); await load(); }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SepaDiagnosticAdmin;
